import { useTranslation } from "react-i18next";

import type { ExtractionProgress } from "@/api/config-server-stages";
import { cn } from "@/lib/utils";

/**
 * Barra única da leitura paginada (spec 2026-10-29, D15): no Gerenciamento e na Extração.
 * Com total estimado, barra determinada com % e "~" (o total é estimativa). Sem total, barra
 * indeterminada e o contador de linhas lidas. Antes do primeiro progresso, só o rótulo.
 */
export function ExtractionProgressBar({
  progress,
  label,
  tone = "light",
  className,
}: {
  progress?: ExtractionProgress | null;
  label: string;
  tone?: "light" | "dark";
  className?: string;
}) {
  const { t, i18n } = useTranslation();
  const format = (value: number) => value.toLocaleString(i18n.language);
  const percent = progress?.percent ?? null;
  const text = !progress
    ? label
    : percent !== null && progress.rows_estimated != null
      ? t("Reading {{table}} · table {{index}} of {{total}} · {{rows}} of ~{{estimated}} rows", {
          table: progress.table,
          index: progress.table_index,
          total: progress.tables_total,
          rows: format(progress.rows_read),
          estimated: format(progress.rows_estimated),
        })
      : t("Reading {{table}} · table {{index}} of {{total}} · {{rows}} rows read", {
          table: progress.table,
          index: progress.table_index,
          total: progress.tables_total,
          rows: format(progress.rows_read),
        });
  const dark = tone === "dark";
  return (
    <div className={cn("flex w-full flex-col gap-1.5", className)}>
      <div className="flex items-center justify-between gap-3">
        <p role="status" className={cn("text-sm", dark ? "text-white/80" : "text-[var(--exed-ink-500)]")}>
          {text}
        </p>
        {percent !== null && (
          <span className={cn("font-mono text-xs", dark ? "text-white" : "text-[var(--exed-ink-900)]")}>
            {percent}%
          </span>
        )}
      </div>
      <div
        role="progressbar"
        aria-label={t("Extraction progress")}
        aria-valuemin={0}
        aria-valuemax={100}
        {...(percent !== null ? { "aria-valuenow": percent } : { "data-indeterminate": "true" })}
        className={cn(
          "relative h-1.5 w-full overflow-hidden rounded-full",
          dark ? "bg-white/[0.12]" : "bg-[var(--exed-card-border)]",
        )}
      >
        {percent !== null ? (
          <div
            className="h-full rounded-full bg-[var(--exed-gold)]"
            style={{ width: `${Math.max(percent, 1)}%` }}
          />
        ) : (
          <div className="absolute inset-y-0 left-0 w-1/3 animate-pulse motion-reduce:animate-none rounded-full bg-[var(--exed-gold)]" />
        )}
      </div>
    </div>
  );
}
